import React, { useState, useEffect } from 'react';
import { formatDateDDMMYYYY } from '../utils/dateUtils';
import DashboardLayout from '../components/DashboardLayout';
import ConfirmModal from '../components/ConfirmModal';
import { getStaffCategories, createStaffCategory, updateStaffCategory, deleteStaffCategory } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface StaffCategory {
  _id: string;
  metadata: {
    name: string;
    displayName?: string;
    createdAt: number;
    updatedAt: number;
    isDeleted: boolean;
  };
  spec: {
    description: string;
    tenantId?: string | null;
  };
}

interface StaffCategoryManagementProps {
  skipLayout?: boolean;
}

const StaffCategoryManagement: React.FC<StaffCategoryManagementProps> = ({ skipLayout = false }) => {
  const { hasPermission } = useAuth();
  const [categories, setCategories] = useState<StaffCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<{ id: string; name: string } | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [editingCategory, setEditingCategory] = useState<StaffCategory | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    description: '',
  });
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState<{ displayName?: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);

  // Capitalize first letter of each word
  const capitalizeWords = (text: string): string => {
    return text
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  useEffect(() => {
    fetchCategories(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce search term - wait 500ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 800);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Close drawer with Escape key
  useEffect(() => {
    if (!showModal) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        const activeElement = document.activeElement as HTMLElement | null;
        if (activeElement && typeof activeElement.blur === 'function') {
          activeElement.blur();
        }
        setShowModal(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showModal]);

  // Refetch when debounced search changes
  useEffect(() => {
    fetchCategories(1, pageSize, debouncedSearchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  const fetchCategories = async (page: number, size: number = pageSize, search: string = debouncedSearchTerm) => {
    try {
      setLoading(true);
      const response = await getStaffCategories(page, size, search && search.trim() ? search.trim() : undefined);
      setCategories(response.data.data || []);

      const pagination = response.data.pagination || {};
      setCurrentPage(pagination.currentPage || page);
      setTotalItems(pagination.totalItems || 0);
      setHasNextPage(pagination.hasNextPage || false);
      setHasPreviousPage(pagination.hasPreviousPage || false);
    } catch (err: any) {
      setError('Failed to load staff categories');
      console.error('Failed to load staff categories:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (category?: StaffCategory) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.metadata.name || '',
        displayName: category.metadata.displayName || category.metadata.name,
        description: category.spec.description || '',
      });
    } else {
      setEditingCategory(null);
      setFormData({
        name: '',
        displayName: '',
        description: '',
      });
    }
    setShowModal(true);
    setError('');
    setValidationErrors({});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError('');
    setValidationErrors({});
    setSubmitting(true);

    if (!formData.displayName.trim()) {
      setValidationErrors({ displayName: 'Display name is required' });
      setError('Please fix the highlighted errors.');
      setSubmitting(false);
      return;
    }

    try {
      if (editingCategory) {
        // When editing, only send displayName (name is immutable)
        const updateData = {
          metadata: {
            displayName: formData.displayName.trim(),
          },
          spec: { description: formData.description.trim() || '' },
        };
        await updateStaffCategory(editingCategory._id, updateData);
      } else {
        // When creating, send displayName and optionally name (if provided, backend will use it; otherwise auto-generate)
        const data: any = {
          metadata: {
            displayName: formData.displayName.trim(),
          },
          spec: { description: formData.description.trim() || '' },
        };
        
        // Include name if provided (otherwise backend will auto-generate from displayName)
        if (formData.name.trim()) {
          data.metadata.name = formData.name.trim();
        }
        
        await createStaffCategory(data);
      }

      setShowModal(false);
      fetchCategories(currentPage);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string, displayName: string) => {
    setCategoryToDelete({ id, name: displayName });
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!categoryToDelete) return;

    try {
      await deleteStaffCategory(categoryToDelete.id);
      const nextPage =
        currentPage > 1 && categories.length === 1 ? currentPage - 1 : currentPage;
      fetchCategories(nextPage);
      setShowDeleteConfirm(false);
      setCategoryToDelete(null);
    } catch (err: any) {
      setErrorMessage(err.response?.data?.message || 'Failed to delete staff category. Unknown error occurred.');
      setShowDeleteConfirm(false);
      setCategoryToDelete(null);
      setShowErrorModal(true);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleRefresh = () => {
    fetchCategories(currentPage, pageSize);
  };

  const content = (
    <>
      {/* <div className="page-header">
        <h2>Staff Category Management</h2>
        <p>Manage staff categories for your organization</p>
      </div> */}

      <div className="card">
        <div className="card-header">
          {/* <h3 className="card-title">Staff Categories</h3> */}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', justifyContent: 'flex-end' }}>
            <input
              type="text"
              placeholder="Search staff categories..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                fontSize: '16px',
                width: '220px',
                flex: '0 0 220px',
              }}
            />
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="refresh-button"
              title="Refresh list"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10"></polyline>
                <polyline points="1 20 1 14 7 14"></polyline>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
              </svg>
              {loading ? 'Loading...' : 'Refresh'}
            </button>
            {hasPermission('staffcategory.create') && (
              <button className="btn-add" onClick={() => handleOpenModal()}>
                + Add Category
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="loading">Loading staff categories...</div>
        ) : categories.length === 0 ? (
          <div className="empty-state">
            <h3>No staff categories yet</h3>
            <p>Create your first staff category to get started</p>
          </div>
        ) : (
          <div style={{ position: 'relative' }}>
            <div className="table-container" style={{ height: 'calc(100vh - 450px)', overflow: 'auto' }}>
              <table className="bookings-table">
                <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                  <tr>
                    <th>Display Name</th>
                    <th>Description</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((category) => {
                    const isSystemLevel = category.spec.tenantId === null || category.spec.tenantId === undefined;
                    return (
                      <tr key={category._id}>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <strong>{category.metadata.displayName || category.metadata.name}</strong>
                          {isSystemLevel && (
                            <span
                              style={{
                                marginLeft: '8px',
                                fontSize: '13px',
                                color: '#667eea',
                                fontWeight: 'bold',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                background: '#e8f5e9',
                              }}
                            >
                              🌍 SYSTEM
                            </span>
                          )}
                        </td>
                        <td>
                          {category.spec.description ? (
                            (() => {
                              const full = category.spec.description;
                              const maxLen = 140;
                              const truncated =
                                full.length > maxLen ? `${full.slice(0, maxLen).trimEnd()}…` : full;
                              return (
                                <span
                                  title={full}
                                  style={{ cursor: full.length > maxLen ? 'pointer' : 'default' }}
                                >
                                  {truncated}
                                </span>
                              );
                            })()
                          ) : (
                            <span style={{ color: '#999', fontStyle: 'italic' }}>No description</span>
                          )}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {formatDateDDMMYYYY(category.metadata.createdAt)}
                        </td>
                        <td>
                          {!isSystemLevel && (
                            <div className="action-buttons">
                              {hasPermission('staffcategory.update') && (
                                <button
                                  className="btn-icon btn-edit"
                                  onClick={() => handleOpenModal(category)}
                                >
                                  Edit
                                </button>
                              )}
                              {hasPermission('staffcategory.delete') && (
                                <button
                                  className="btn-icon btn-delete"
                                  onClick={() =>
                                    handleDelete(
                                      category._id,
                                      category.metadata.displayName || category.metadata.name
                                    )
                                  }
                                >
                                  Delete
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

              <div className="pagination-controls" style={{ position: 'relative', width: '100%' }}>
              <div className="pagination-left" style={{ position: 'absolute', right: 250, whiteSpace: 'nowrap', zIndex: 10 }}>
                <label style={{ fontSize: '16px', whiteSpace: 'nowrap' }}>
                  Rows per page:{' '}
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      const newSize = Number(e.target.value);
                      setPageSize(newSize);
                      fetchCategories(1, newSize);
                    }}
                    style={{ minWidth: '60px', width: 'auto' }}
                  >
                    {[10, 25, 50, 100, 500].map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="pagination-center">
                <span className="pagination-info">
                  {totalItems === 0
                    ? '0 of 0'
                    : `${(currentPage - 1) * pageSize + 1}–${Math.min(
                        currentPage * pageSize,
                        totalItems
                      )} of ${totalItems}`}
                </span>
              </div>
              <div className="pagination-right">
                <button
                  className="btn-secondary"
                  disabled={!hasPreviousPage || loading}
                  onClick={() => hasPreviousPage && fetchCategories(currentPage - 1)}
                >
                  ‹
                </button>
                <button
                  className="btn-secondary"
                  disabled={!hasNextPage || loading}
                  onClick={() => hasNextPage && fetchCategories(currentPage + 1)}
                >
                  ›
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div className="drawer-overlay" onClick={() => setShowModal(false)}>
          <div className="drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <h3 className="drawer-title">
                {editingCategory ? 'Edit Staff Category' : 'Create New Staff Category'}
              </h3>
              <button className="drawer-close" onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="drawer-form">
              <div className="drawer-body">
                {error && <div className="error-message">{error}</div>}

                <div className="form-group">
                  <label>
                    Display Name
                    <span className="required-star">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.displayName}
                    onChange={(e) => {
                      const capitalized = capitalizeWords(e.target.value);
                      setFormData({ ...formData, displayName: capitalized });
                    }}
                    className={validationErrors.displayName ? 'input-error' : ''}
                    placeholder="e.g., Medical Doctor, Registered Nurse, Emergency Paramedic"
                  />
                  {validationErrors.displayName && (
                    <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      {validationErrors.displayName}
                    </small>
                  )}
                  <small style={{ color: '#666', fontSize: '14px' }}>
                    The name shown in the user interface. This can be edited later.
                  </small>
                </div>

                {editingCategory && (
                <div className="form-group">
                    <label>Internal Name (Read-only)</label>
                  <input
                    type="text"
                    value={formData.name}
                      disabled
                      placeholder={formData.name}
                    style={{
                        opacity: 0.6,
                        cursor: 'not-allowed',
                    }}
                  />
                  <small style={{ color: '#666', fontSize: '14px' }}>
                      Internal identifier cannot be changed after creation.
                  </small>
                </div>
                )}

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Enter a description for this category (optional)"
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '16px',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                    }}
                  />
                </div>
              </div>

              <div className="drawer-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)} disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit" disabled={submitting}>
                  {submitting ? (
                    <>
                      <span className="loading-spinner btn-spinner" aria-hidden />
                      {editingCategory ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    `${editingCategory ? 'Update' : 'Create'} Category`
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Delete Staff Category"
        message={`Are you sure you want to delete the staff category "${categoryToDelete?.name}"? This action cannot be undone.`}
        confirmText="Yes, Delete"
        cancelText="Cancel"
        type="danger"
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setCategoryToDelete(null);
        }}
      />

      <ConfirmModal
        isOpen={showErrorModal}
        title="Error"
        message={errorMessage}
        confirmText="OK"
        cancelText=""
        type="danger"
        onConfirm={() => setShowErrorModal(false)}
        onCancel={() => setShowErrorModal(false)}
      />
    </>
  );

  if (skipLayout) {
    return content;
  }

  return <DashboardLayout>{content}</DashboardLayout>;
};

export default StaffCategoryManagement;
